import React, { useEffect } from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
import styled from 'styled-components';
import FallbackTopics from 'assets/images/fallback-topics.svg';
import { useIntl } from 'react-intl-next';
import { di } from 'react-magnetic-di';
import { graphql, useFragment } from 'react-relay';
import { ElementRoot, ElementWidth } from 'view/common/element-root';
import topicsMessages from 'view/help-center-topics/topics/messages';
import { useIsPageInEditMode, useIsPageInPreviewMode } from 'view/layout-next/controllers';
import * as bp from 'view/styles/breakpoints';
import * as elevation from 'view/styles/elevation';
import * as grid from 'view/styles/grid';
import { Title } from 'view/styles/help-center';
import * as mixins from 'view/styles/mixins';
import { AnalyticsContext, useAnalyticsEvents } from '@atlaskit/analytics-next';
import EmptyState from '@atlaskit/empty-state';
import { Box, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { UI_EVENT_TYPE } from '@atlassian/analytics-web-react';
import type { listOfTopicsElementFragment$key } from './__generated__/listOfTopicsElementFragment.graphql';
import messages from './messages';
import Topic from './topic/topic';

interface ListOfTopicsProps {
    topicsElement: listOfTopicsElementFragment$key;
}
export const ListOfTopics: React.FC<ListOfTopicsProps> = ({ topicsElement }) => {
    di(useIsPageInEditMode);
    di(useIsPageInPreviewMode);

    const data = useFragment<listOfTopicsElementFragment$key>(
        graphql`
            fragment listOfTopicsElementFragment on HelpLayoutTopicsListElement {
                topics {
                    topicId
                    hidden
                    items {
                        __typename
                    }
                    ...topicCardFragment
                }
            }
        `,
        topicsElement
    );

    const intl = useIntl();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const [isPageInEditMode] = useIsPageInEditMode();
    const [isPageInPreviewMode] = useIsPageInPreviewMode();

    const topics = data?.topics || [];

    const visibleTopics = topics.filter((topic) => !topic.hidden && topic.items?.length);

    useEffect(() => {
        if (!isPageInEditMode && visibleTopics.length > 0) {
            const event = createAnalyticsEvent({
                analyticsType: UI_EVENT_TYPE,
                action: 'viewed',
                topics_count: visibleTopics.length,
            });
            event.fire();
        }
    }, [createAnalyticsEvent, visibleTopics.length, isPageInEditMode]);

    if (visibleTopics.length === 0) {
        if (isPageInEditMode && !isPageInPreviewMode) {
            return (
                <Box xcss={nonDraggableStyle}>
                    <EmptyState
                        header=""
                        maxImageWidth={250}
                        description={intl.formatMessage(messages.emptyStateDescription)}
                        imageUrl={FallbackTopics}
                        width="wide"
                    />
                </Box>
            );
        }
        return null;
    }

    return (
        <ElementRoot elementWidth={ElementWidth.COMPACT}>
            <Title>{intl.formatMessage(topicsMessages.topicsSectionLabel)}</Title>
            <TopicsSection count={visibleTopics.length}>
                <TopicsList count={visibleTopics.length}>
                    {visibleTopics.map((topic, index) => (
                        <ListItem key={topic.topicId}>
                            <AnalyticsContext data={{ attributes: { topicPosition: index + 1 } }}>
                                <Topic topic={topic} />
                            </AnalyticsContext>
                        </ListItem>
                    ))}
                </TopicsList>
            </TopicsSection>
        </ElementRoot>
    );
};

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const TopicsSection = styled.div<{ count: number }>`
    width: ${grid.multiple(33).px};
    margin-left: auto;
    margin-right: auto;
    ${bp.fromXSmall.css`
        width: 100%;
    `};
    ${bp.fromMedium.css`
        width: ${({ count }) => {
            if (count > 1) {
                return '100%';
            }
            return `${grid.multiple(42).px}`;
        }};
        margin-left: 0;
    `};
    border-radius: ${mixins.borderRadiusValue};
    background-color: ${token('elevation.surface.raised', colors.N0)};
    ${elevation.e100}
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const StyledList = styled.ol<{ count: number }>`
    box-sizing: border-box;
    list-style-type: none;
    display: flex;
    flex-flow: row wrap;
    gap: ${token('space.400', '32px')};
    width: 100%;
    padding: ${token('space.400', '32px')};
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const TopicsList = styled(StyledList)<{ count: number }>`
    ${bp.fromMedium.css`
    & > li {
        flex-basis: auto;
        width: ${({ count }) => {
            if (count > 2) {
                return 'calc((100% - 64px) / 3)';
            }
            if (count === 2) {
                return 'calc((100% - 64px) / 2)';
            }
            return '100%';
        }};
    };`}
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const ListItem = styled.li`
    margin-top: 0;
    box-sizing: border-box;
    width: 100%;
`;

const nonDraggableStyle = xcss({
    pointerEvents: 'none',
});
